import { MoreHorizontal } from 'lucide-react';
import { useEffect, useState } from 'react';

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle
} from '@/components/ui/sheet';

import { Section, SectionsNav, isPlainClick } from './sections';

/**
 * Phone: the sections as a bottom tab bar (below 768px only). The first four
 * are tabs; any further section sits behind a fifth tab, More, which opens a
 * sheet, so the bar never has to squeeze six labels into 375px.
 *
 * The bar is fixed to the bottom of the screen; pages make room for it
 * through --sig-tabbar-h (index.css), which is only non-zero while the bar
 * is visible. It steps aside while the keyboard is up — a text field has
 * focus — because on a phone it would otherwise sit on top of the keyboard
 * and eat a third of what is left of the screen while the user types.
 */
const TYPING_SELECTOR =
  'input:not([type=checkbox]):not([type=radio]):not([type=button]), textarea, [contenteditable="true"]';

const TABS = 4;

export default function MobileTabBar({ nav }: { nav: SectionsNav }) {
  const { sections, active, go, hrefOf } = nav;
  const [typing, setTyping] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);

  useEffect(() => {
    const onFocusIn = (e: FocusEvent) => {
      const el = e.target as Element | null;
      if (el?.matches?.(TYPING_SELECTOR)) setTyping(true);
    };
    const onFocusOut = () => {
      // Focus may move straight to another field; check after it lands.
      window.setTimeout(() => {
        const el = document.activeElement as Element | null;
        setTyping(!!el?.matches?.(TYPING_SELECTOR));
      }, 0);
    };
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    return () => {
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
    };
  }, []);

  if (sections.length < 2) return null;

  // Up to four tabs; with a fifth section or more, the rest go behind More.
  const primary = sections.length > TABS ? sections.slice(0, TABS) : sections;
  const more = sections.length > TABS ? sections.slice(TABS) : [];
  const moreActive = more.some((s) => s.key === active);

  const tab = (s: Section) => {
    const on = s.key === active;
    const Icon = s.icon;
    return (
      <a
        key={s.key}
        href={hrefOf(s)}
        aria-current={on ? 'page' : undefined}
        onClick={(e) => {
          if (!isPlainClick(e)) return;
          e.preventDefault();
          setMoreOpen(false);
          go(s);
        }}
        className={
          'relative flex h-14 flex-1 flex-col items-center justify-center gap-0.5 text-[11px] font-medium ' +
          (on ? 'text-primary' : 'text-muted-foreground')
        }
      >
        <Icon className="size-[22px]" aria-hidden="true" />
        {s.shortLabel ?? s.label}
        {s.badge && s.badge > 0 ? (
          <span
            dir="ltr"
            className="absolute top-1.5 left-1/2 ml-2 rounded-full px-1.5 text-[10px] font-bold leading-4 text-white"
            style={{ background: 'hsl(var(--destructive))' }}
          >
            {s.badge}
          </span>
        ) : null}
      </a>
    );
  };

  return (
    <>
      <nav
        id="sig-tabbar"
        data-visible={typing ? 'false' : 'true'}
        aria-label="Signals sections"
        className={
          'md:hidden fixed inset-x-0 bottom-0 z-40 border-t bg-background ' +
          (typing ? 'hidden' : 'flex')
        }
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        {primary.map(tab)}
        {more.length ? (
          <button
            type="button"
            aria-haspopup="dialog"
            aria-expanded={moreOpen}
            onClick={() => setMoreOpen(true)}
            className={
              'flex h-14 flex-1 flex-col items-center justify-center gap-0.5 text-[11px] font-medium ' +
              (moreActive ? 'text-primary' : 'text-muted-foreground')
            }
          >
            <MoreHorizontal className="size-[22px]" aria-hidden="true" />
            More
          </button>
        ) : null}
      </nav>
      {more.length ? (
        <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
          <SheetContent side="bottom" className="rounded-t-2xl pb-8">
            <SheetTitle className="text-sm text-muted-foreground">
              More
            </SheetTitle>
            <SheetDescription className="sr-only">
              The other sections of Signals.
            </SheetDescription>
            <div className="mt-3 grid grid-cols-3 gap-2">
              {more.map((s) => {
                const on = s.key === active;
                const Icon = s.icon;
                return (
                  <a
                    key={s.key}
                    href={hrefOf(s)}
                    aria-current={on ? 'page' : undefined}
                    onClick={(e) => {
                      if (!isPlainClick(e)) return;
                      e.preventDefault();
                      setMoreOpen(false);
                      go(s);
                    }}
                    className={
                      'flex min-h-[4.5rem] flex-col items-center justify-center gap-2 rounded-xl px-2 py-3 text-center text-xs font-semibold ' +
                      (on
                        ? 'bg-primary/10 text-primary'
                        : 'bg-muted text-foreground')
                    }
                  >
                    <Icon className="size-[22px]" aria-hidden="true" />
                    {s.label}
                  </a>
                );
              })}
            </div>
          </SheetContent>
        </Sheet>
      ) : null}
    </>
  );
}
